import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchWorkspaceTeam } from '@/features/workspace/workspaceThunks';
import {
  createPresenter,
  deletePresenter,
  listPresenters,
  listSpeakers,
  updatePresenter,
} from '@/features/webinar/webinarThunks';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Loader from '@/components/ui/Loader';
import type { Webinar } from '@/features/webinar/types';
import { useTranslation } from 'react-i18next';
import { mapHttpError } from '@/lib/mapApiError';
import type { AppError } from '@/features/auth/types';
import { toast } from 'react-toastify';
import { selectUsage } from '@/features/billing/billingSlice';
import LiveRolesUsageBanner from '@/components/billing/LiveRolesUsageBanner';
import { getLiveRolesMax, isLiveRolesAtMax, wouldExceedLiveRolesLimit } from '@/lib/liveRolesLimit';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';

interface PresentersFormProps {
  webinar?: Webinar | null;
  isEdit?: boolean;
  onSuccess?: () => void;
}

const MAX_OVERRIDE_FULL_NAME_LENGTH = 30;

const presenterItemSchema = z.object({
  presenterId: z.number().optional(),
  userId: z.string().min(1),
  overrideFullName: z.string().max(MAX_OVERRIDE_FULL_NAME_LENGTH).optional(),
});

type PresenterFormItem = z.infer<typeof presenterItemSchema>;
type PresentersFormValues = {
  presenters: PresenterFormItem[];
};

export default function PresenterForm({ webinar, isEdit = false, onSuccess }: PresentersFormProps) {
  void isEdit;
  const { t } = useTranslation('createWebinar');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const team = useAppSelector((state) => state.workspace.team);
  const usage = useAppSelector(selectUsage);
  const maxLiveRoles = getLiveRolesMax(usage);
  const allPresenters = useAppSelector((state) => state.webinar.presenters);
  const allSpeakers = useAppSelector((state) => state.webinar.speakers);
  const moderators = useAppSelector((state) => state.webinar.moderators);
  const webinarPresenters = useMemo(
    () => allPresenters.filter((p) => (webinar?.id ? p.webinarId === webinar.id : false)),
    [allPresenters, webinar?.id],
  );
  const webinarModerators = useMemo(
    () => moderators.filter((m) => (webinar?.id ? m.webinarId === webinar.id : false)),
    [moderators, webinar?.id],
  );
  const webinarSpeakers = useMemo(
    () => allSpeakers.filter((s) => (webinar?.id ? s.webinarId === webinar.id : false)),
    [allSpeakers, webinar?.id],
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      void dispatch(fetchWorkspaceTeam({ workspaceId, page: 0, size: 50 }));
    }
  }, [workspaceId, dispatch]);

  useEffect(() => {
    if (workspaceId && webinar?.id) {
      void dispatch(listPresenters({ workspaceId, webinarId: webinar.id }));
      void dispatch(listSpeakers({ workspaceId, webinarId: webinar.id }));
    }
  }, [workspaceId, webinar?.id, dispatch]);

  const initialPresenterIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    initialPresenterIdsRef.current = new Set(webinarPresenters.map((p) => p.id));
  }, [webinarPresenters]);

  const getTeamMemberDisplayName = (userId: string) => {
    const member = team.find((m) => m.userId === userId);
    if (!member) return userId;
    return `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || userId;
  };

  const formPresenters = useMemo(
    () =>
      webinarPresenters.map<PresenterFormItem>((p) => ({
        presenterId: p.id,
        userId: p.userId,
        overrideFullName: p.overrideFullName?.trim()
          ? p.overrideFullName
          : getTeamMemberDisplayName(p.userId),
      })),
    [webinarPresenters, team],
  );

  const { handleSubmit, control, reset, watch, setValue } = useForm<PresentersFormValues>({
    resolver: zodResolver(
      z.object({
        presenters: z.array(presenterItemSchema),
      }),
    ),
    defaultValues: {
      presenters: formPresenters,
    },
    mode: 'onChange',
  });

  const lastResetValueRef = useRef<string>('');

  useEffect(() => {
    if (!webinar?.id) return;

    const currentValue = JSON.stringify(formPresenters);
    if (lastResetValueRef.current !== currentValue) {
      lastResetValueRef.current = currentValue;
      reset({
        presenters: formPresenters,
      });
    }
  }, [webinar?.id, formPresenters, reset, isSubmitting]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'presenters',
  });

  const moderatorUserIds = useMemo(
    () => new Set(webinarModerators.map((m) => m.userId)),
    [webinarModerators],
  );
  const availableTeamForSelect = useMemo(
    () =>
      team.filter(
        (member) =>
          !moderatorUserIds.has(member.userId) && !fields.some((f) => f.userId === member.userId),
      ),
    [team, moderatorUserIds, fields],
  );

  const guestCount = webinarSpeakers.length;
  const presenterCount = fields.length;
  const isAtMax = isLiveRolesAtMax(presenterCount + guestCount, maxLiveRoles);

  const buildPresenterInput = (item: PresenterFormItem) => {
    const memberDisplayName = getTeamMemberDisplayName(item.userId);
    const trimmedOverride = item.overrideFullName?.trim() ?? '';
    const hasCustomOverride = trimmedOverride.length > 0 && trimmedOverride !== memberDisplayName;

    return {
      jobTitle: t('presentersForm.jobTitle'),
      userId: item.userId,
      overrideFullName: hasCustomOverride ? trimmedOverride : null,
    };
  };

  const handlePresenterError = (error: AppError | undefined, fallbackMessage: string) => {
    const isUserAlreadyModerator =
      error?.details &&
      Array.isArray(error.details) &&
      error.details.some(
        (detail: { message?: string; fieldName?: string }) =>
          detail.message === 'HTTP.ERROR.400.USER.ALREADY_ASSIGNED_TO_ROLE' &&
          detail.fieldName === 'userId',
      );

    const mainMessage = isUserAlreadyModerator
      ? t('presentersForm.validation.alreadyModerator')
      : error?.message
        ? tCommon(error.message, error.message)
        : tCommon('errors.generic', fallbackMessage);
    const rawDetails =
      !isUserAlreadyModerator && Array.isArray(error?.details) ? error.details : [];
    const detailMessages = rawDetails
      .map((d: { message?: string }) =>
        d.message ? tCommon(mapHttpError(d.message), d.message) : '',
      )
      .filter(Boolean);
    toast.error([mainMessage, ...detailMessages].join(' • '));
  };

  const onSubmit = async (data: PresentersFormValues) => {
    if (!workspaceId || !webinar?.id) return;

    if (wouldExceedLiveRolesLimit(data.presenters.length, guestCount, maxLiveRoles)) {
      toast.error(t('liveRolesLimit.exceeded'));
      return;
    }

    setIsSubmitting(true);
    try {
      const currentPresenterIds = new Set(
        data.presenters
          .map((item) => item.presenterId)
          .filter((id): id is number => id !== undefined),
      );

      const presentersToDelete = Array.from(initialPresenterIdsRef.current).filter(
        (id) => !currentPresenterIds.has(id),
      );

      for (const presenterIdToDelete of presentersToDelete) {
        const deleteResult = await dispatch(
          deletePresenter({
            workspaceId,
            webinarId: webinar.id,
            presenterId: presenterIdToDelete,
          }),
        );

        if (deletePresenter.rejected.match(deleteResult)) {
          const error = deleteResult.payload as AppError | undefined;
          const mainMessage = error?.message
            ? tCommon(error.message, error.message)
            : tCommon('errors.generic', 'Une erreur est survenue lors de la suppression.');
          const rawDetails = Array.isArray(error?.details) ? error.details : [];
          const detailMessages = rawDetails
            .map((d: { message?: string }) =>
              d.message ? tCommon(mapHttpError(d.message), d.message) : '',
            )
            .filter(Boolean);
          const fullMessage = [mainMessage, ...detailMessages].join(' • ');
          toast.error(fullMessage);
          return;
        }
      }

      for (const item of data.presenters) {
        const input = buildPresenterInput(item);

        if (!item.presenterId) {
          const createResult = await dispatch(
            createPresenter({
              workspaceId,
              webinarId: webinar.id,
              input,
            }),
          );

          if (createPresenter.rejected.match(createResult)) {
            handlePresenterError(
              createResult.payload as AppError | undefined,
              'Une erreur est survenue lors de la création.',
            );
            return;
          }
          continue;
        }

        const existingPresenter = webinarPresenters.find((p) => p.id === item.presenterId);
        const existingOverride = existingPresenter?.overrideFullName?.trim() ?? '';
        const nextOverride = input.overrideFullName?.trim() ?? '';

        if (existingOverride !== nextOverride) {
          const updateResult = await dispatch(
            updatePresenter({
              workspaceId,
              webinarId: webinar.id,
              presenterId: item.presenterId,
              input,
            }),
          );

          if (updatePresenter.rejected.match(updateResult)) {
            handlePresenterError(
              updateResult.payload as AppError | undefined,
              'Une erreur est survenue lors de la mise à jour.',
            );
            return;
          }
        }
      }

      toast.success(t('presentersForm.saveSuccess'));
      onSuccess?.();

      initialPresenterIdsRef.current = new Set(
        data.presenters
          .map((item) => item.presenterId)
          .filter((id): id is number => id !== undefined),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddPresenter = (userId: string) => {
    if (isAtMax) {
      toast.error(t('liveRolesLimit.exceeded'));
      return;
    }
    const isAlreadyPresent = fields.some((field) => field.userId === userId);
    if (isAlreadyPresent) {
      return;
    }
    append({
      presenterId: undefined,
      userId,
      overrideFullName: getTeamMemberDisplayName(userId),
    });
  };

  const handleRemovePresenter = (index: number) => {
    remove(index);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <LiveRolesUsageBanner
        max={maxLiveRoles}
        presentersCount={presenterCount}
        guestsCount={guestCount}
      />
      <div className="flex flex-col gap-4">
        <Select onValueChange={handleAddPresenter} disabled={isAtMax}>
          <SelectTrigger label={t('presentersForm.addPresenter')} className="">
            <SelectValue placeholder={t('presentersForm.addPresenter')} />
          </SelectTrigger>
          <SelectContent>
            {availableTeamForSelect.map((member) => (
              <SelectItem key={member.userId} value={member.userId}>
                {member.firstName} {member.lastName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex flex-col gap-4">
          {fields.map((field, index) => {
            const memberDisplayName = getTeamMemberDisplayName(field.userId);
            const overrideFullNameValue = watch(`presenters.${index}.overrideFullName`) ?? '';

            return (
              <div key={field.id} className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-1 items-center justify-between rounded-lg border bg-white border-input px-4">
                    <div className="flex flex-col justify-center h-12">
                      <span className="text-base">{memberDisplayName}</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemovePresenter(index)}
                    className="p-2 h-12 shrink-0 cursor-pointer border rounded-lg border-input bg-[#FBFBFB] disabled:cursor-not-allowed disabled:bg-white disabled:border-input"
                  >
                    <X className="size-5 text-red-500" />
                  </button>
                </div>

                <TextFieldWithLimit
                  label={t('presentersForm.overrideFullName')}
                  value={overrideFullNameValue}
                  placeholder={memberDisplayName}
                  onChange={(v) =>
                    setValue(`presenters.${index}.overrideFullName`, v, { shouldDirty: true })
                  }
                  maxLength={MAX_OVERRIDE_FULL_NAME_LENGTH}
                />
              </div>
            );
          })}
        </div>

        <Button type="submit" disabled={isSubmitting || fields.length === 0}>
          {isSubmitting && <Loader />}
          {t('save')}
        </Button>
      </div>
    </form>
  );
}
